import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Drawer,
  Group,
  Loader,
  NumberInput,
  Paper,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import { IconCalculator, IconPlus } from '@tabler/icons-react';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { ConfirmDialog } from '../../components/ConfirmDialog';

// Phase Accounting-FA: الأصول الثابتة — register assets (acquisition
// posts to the GL) and run the month's straight-line depreciation.

interface FixedAssetDto {
  fixedAssetId: string;
  nameAr: string;
  categoryAr: string | null;
  acquisitionDate: string;
  cost: number;
  salvageValue: number;
  usefulLifeMonths: number;
  monthlyDepreciation: number;
  accumulatedDepreciation: number;
  bookValue: number;
  status: number;
}

const QK = ['fixed-assets'] as const;

export function FixedAssetsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmDepr, setConfirmDepr] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: QK,
    queryFn: async () =>
      (await apiClient.get<FixedAssetDto[]>('/api/accounting/fixed-assets')).data,
  });

  const now = new Date();
  const depreciate = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post('/api/accounting/fixed-assets/depreciate', {
          year: now.getFullYear(),
          month: now.getMonth() + 1,
        })
      ).data as { assetsDepreciated: number; totalDepreciation: number },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: QK });
      notifications.show({
        color: r.assetsDepreciated > 0 ? 'green' : 'yellow',
        title: t('accounting.fixedAssets.deprDone', {
          count: r.assetsDepreciated,
          total: r.totalDepreciation.toFixed(2),
        }),
        message: '',
      });
    },
    onError: (err) =>
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      }),
  });

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.accounting')}
        title={t('accounting.fixedAssets.title')}
        subtitle={t('accounting.fixedAssets.subtitle')}
      />

      <Paper p="md">
        <Group justify="space-between">
          <Button
            variant="light"
            leftSection={<IconCalculator size={16} stroke={1.6} />}
            onClick={() => setConfirmDepr(true)}
          >
            {t('accounting.fixedAssets.runDepr', {
              month: String(now.getMonth() + 1).padStart(2, '0'),
              year: now.getFullYear(),
            })}
          </Button>
          <Button leftSection={<IconPlus size={16} stroke={1.6} />} onClick={() => setCreateOpen(true)}>
            {t('accounting.fixedAssets.create')}
          </Button>
        </Group>
      </Paper>

      {isLoading ? (
        <Stack align="center" py={48}><Loader size="sm" /></Stack>
      ) : (
        <Paper p={0}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('accounting.fixedAssets.columns.name')}</Table.Th>
                <Table.Th>{t('accounting.fixedAssets.columns.acquired')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('accounting.fixedAssets.columns.cost')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('accounting.fixedAssets.columns.monthly')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('accounting.fixedAssets.columns.accumulated')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('accounting.fixedAssets.columns.book')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(data ?? []).map((a) => (
                <Table.Tr key={a.fixedAssetId}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{a.nameAr}</Text>
                    {a.categoryAr && <Text size="xs" c="dimmed">{a.categoryAr}</Text>}
                  </Table.Td>
                  <Table.Td className="font-numeric">{a.acquisitionDate}</Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                    {a.cost.toFixed(2)}
                  </Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                    {a.monthlyDepreciation.toFixed(2)}
                  </Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                    {a.accumulatedDepreciation.toFixed(2)}
                  </Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end', fontWeight: 600 }}>
                    {a.bookValue.toFixed(2)}
                  </Table.Td>
                </Table.Tr>
              ))}
              {data && data.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text size="sm" c="dimmed" ta="center" py="lg">
                      {t('accounting.fixedAssets.empty')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <CreateAssetDrawer
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => qc.invalidateQueries({ queryKey: QK })}
      />
      <ConfirmDialog
        opened={confirmDepr}
        onClose={() => setConfirmDepr(false)}
        onConfirm={() => { setConfirmDepr(false); depreciate.mutate(); }}
        title={t('accounting.fixedAssets.deprConfirmTitle')}
        message={t('accounting.fixedAssets.deprConfirmMessage')}
        confirmColor="green"
        loading={depreciate.isPending}
      />
    </Stack>
  );
}

function CreateAssetDrawer({
  opened, onClose, onCreated,
}: { opened: boolean; onClose: () => void; onCreated: () => void }) {
  const { t, i18n } = useTranslation();
  const [nameAr, setNameAr] = useState('');
  const [category, setCategory] = useState('');
  const [date, setDate] = useState<Date | null>(new Date());
  const [cost, setCost] = useState<number>(0);
  const [salvage, setSalvage] = useState<number>(0);
  const [months, setMonths] = useState<number>(36);
  const [postAcquisition, setPostAcquisition] = useState(true);

  const create = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post('/api/accounting/fixed-assets', {
          nameAr: nameAr.trim(),
          nameEn: null,
          categoryAr: category.trim() || null,
          acquisitionDate: toIsoDate(date!),
          cost,
          salvageValue: salvage,
          usefulLifeMonths: months,
          branchId: null,
          notes: null,
          postAcquisition,
        })
      ).data,
  });

  const monthly = months > 0 ? Math.max(0, (cost - salvage) / months) : 0;
  const valid = nameAr.trim() !== '' && cost > 0 && salvage >= 0 && salvage < cost
    && months >= 1 && date !== null;

  const submit = async () => {
    try {
      await create.mutateAsync();
      notifications.show({ color: 'green', title: t('common.toasts.created'), message: nameAr });
      onCreated();
      onClose();
      setNameAr(''); setCategory(''); setCost(0); setSalvage(0);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={t('accounting.fixedAssets.create')}
      position={i18n.language === 'ar' ? 'left' : 'right'}
      size="md"
      padding="lg"
    >
      <Stack gap="md">
        <TextInput label={t('accounting.fixedAssets.form.nameLabel')} withAsterisk
          value={nameAr} onChange={(e) => setNameAr(e.currentTarget.value)} />
        <TextInput label={t('accounting.fixedAssets.form.categoryLabel')}
          value={category} onChange={(e) => setCategory(e.currentTarget.value)} />
        <DatePickerInput label={t('accounting.fixedAssets.form.dateLabel')}
          value={date} onChange={setDate} />
        <Group grow>
          <NumberInput label={t('accounting.fixedAssets.form.costLabel')}
            value={cost} onChange={(v) => setCost(Number(v) || 0)}
            min={0} decimalScale={2} hideControls />
          <NumberInput label={t('accounting.fixedAssets.form.salvageLabel')}
            value={salvage} onChange={(v) => setSalvage(Number(v) || 0)}
            min={0} decimalScale={2} hideControls />
        </Group>
        <NumberInput label={t('accounting.fixedAssets.form.monthsLabel')}
          description={t('accounting.fixedAssets.form.monthsHelp', { monthly: monthly.toFixed(2) })}
          value={months} onChange={(v) => setMonths(Number(v) || 0)}
          min={1} max={600} />
        <Switch
          label={t('accounting.fixedAssets.form.postAcquisitionLabel')}
          description={t('accounting.fixedAssets.form.postAcquisitionHelp')}
          checked={postAcquisition}
          onChange={(e) => setPostAcquisition(e.currentTarget.checked)}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={create.isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={!valid} loading={create.isPending}>
            {t('common.create')}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
